import { reactive } from 'vue';
import type { CanvasEx } from '@/typings/canvas';

// Fields are optional: in bulk mode only values shared across the whole selection (non-"Mixed") are copied
export interface DimensionsClip {
    widthPx?: number;
    heightPx?: number;
    sourceType?: string;
}

export interface PositionClip {
    centerXPx?: number;
    centerYPx?: number;
    rotation?: number;
    skewX?: number;
    skewY?: number;
    sourceType?: string;
}

interface TransformClipboardState {
    canvas: CanvasEx | null;
    dimensions: DimensionsClip | null;
    position: PositionClip | null;
}

/**
 * Module-level singleton. Every `<vue-component>` in the Angular templates mounts its own Vue
 * root, but they all import this same module (just like the shared Vuex store), so the clipboard
 * survives across the separate ElementTransformProperties instances and across element-type
 * selections within a step.
 */
const state = reactive<TransformClipboardState>({
    canvas: null,
    dimensions: null,
    position: null
});

export const useTransformClipboard = () => {
    /**
     * Ties the clipboard to a canvas. Each create-product step owns a distinct fabric canvas, so
     * when a component mounts with a different canvas than the stored one (navigating between
     * steps) the clipboard is cleared — values never paste across canvases.
     */
    const bindCanvas = (canvas?: CanvasEx | null) => {
        if (!canvas) return;
        if (state.canvas && state.canvas !== canvas) {
            state.dimensions = null;
            state.position = null;
        }
        state.canvas = canvas;
    };

    const copyDimensions = (canvas: CanvasEx, data: DimensionsClip) => {
        bindCanvas(canvas);
        state.dimensions = { ...data };
    };

    const copyPosition = (canvas: CanvasEx, data: PositionClip) => {
        bindCanvas(canvas);
        state.position = { ...data };
    };

    const dimensionsFor = (canvas?: CanvasEx | null): DimensionsClip | null =>
        state.dimensions && state.canvas === canvas ? state.dimensions : null;

    const positionFor = (canvas?: CanvasEx | null): PositionClip | null =>
        state.position && state.canvas === canvas ? state.position : null;

    return {
        state,
        bindCanvas,
        copyDimensions,
        copyPosition,
        dimensionsFor,
        positionFor
    };
};
